import React from "react";
import { connect } from "react-redux";
import update from "immutability-helper";
import { I18n } from "@aws-amplify/core";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/styles";
import {
  get,
  isEmpty,
  filter,
  size,
  parseInt,
  map,
  flatten,
  indexOf,
  forEach,
} from "lodash";
import Grid from "@material-ui/core/Grid";

import { fetchGet, fetchPost } from "../../api/fetch";
import Tab from "./Tab";
import SiteLog from "./SiteLog";
import TabHistory from "./TabHistory";
import SiteHistory from "./SiteHistory";
import AddDriveInOut from "./AddEntry/AddDriveInOut";
import AddWalkInOut from "./AddEntry/AddWalkInOut";
import LogDetails from "./LogDetails/LogDetails";
import LiveEventsWalkInOut from "./LiveEvents/LiveEventsWalkInOut";
import LiveEventsDriveInOut from "./LiveEvents/LiveEventsDriveInOut";
import SiteEntranceExitForm from "./SiteEntranceExitForm";
import { setBreadcrumb } from "../../actions/common";
import { selectSite, selectTab } from "../../actions/siteEntranceExit/";
import { fetchCompanies } from "../../actions/siteEntranceExit/companies";
import { updateLiveEvents } from "../../actions/siteEntranceExit/liveEvents";
import {
  removeTab,
  getLatestLog,
  addTab,
} from "../../actions/siteEntranceExit/tabs";
import { handleSiteSelect } from "../../utils/site";
import { getDetection, liveEventTimeout } from "../../utils/liveEvents";
import {
  LOGS_TAB,
  STATIC_TAB,
  DYNAMIC_TAB,
} from "../../constants/siteEntranceExit";
import { absent } from "../../constants/common";
import { eventGroupsData } from "../../constants/eventGroups";
import { driveInOut, walkInOut, trainInOut } from "../../constants/reducers";
import colors from "../../assets/style/colors";
import TrainDashboard from "../TrainManifest/TrainDashboard";

const styles = {
  mainContainer: {
  },
  tabContainer: {
    paddingLeft: "16px",
  },
  link: {
    textDecoration: "none",
  },
  tab: {
    minWidth: "120px",
    backgroundColor: colors.tableDarkBg,
    border: "1px solid " + colors.veryLighPink,
    borderBottom: "none !important",
    borderRadius: "2px",
    textAlign: "center",
    marginRight: "10px",
    padding: "4px 12px",
    cursor: "pointer",
    color: colors.black54,
    height: "32px",
  },
  activeTab: {
    backgroundColor: colors.white,
    fontWeight: "500",
    borderTop: "3px solid " + colors.primary,
    paddingTop: "2px",
    color: colors.black87,
    height: "34px",
  },
  refresh: {
    "& img": {
      marginLeft: "10px",
      width: "20px",
      cursor: "pointer",
    },
  },
};

const SiteEntranceExit = (props) => {
  const { classes, dispatch } = props;

  const [section, setSection] = React.useState(props.section || driveInOut);
  const [initialized, setInitialized] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState();
  const [zone, setZone] = React.useState();

  const activeSite = get(props, ["siteEntranceExit", section, "activeSite"]);
  const tabs = get(props, ["siteEntranceExit", section, "tabs"]);

  const siteId = parseInt(get(props, ["match", "params", "siteId"]));
  const zoneId = parseInt(get(props, ["match", "params", "zoneId"]));
  const eventGroupId = parseInt(
    get(props, ["match", "params", "eventGroupId"])
  );

  const companiesData = get(props, [
    "siteEntranceExit",
    section,
    "companies",
    "data",
  ]);

  React.useEffect(() => {
    if (initialized) {
      console.log("SiteEntranceExit >> Things were already setup, re-initializing.")
      // No site ID, set one by default
      if (isNaN(siteId) && companiesData) {
        console.log("                 >> Site not set and we have a company, so navigate to the default site.")
        let defaultSite = get(companiesData, [0, "sites", 0, "id"]);
        props.history.push(
          `/siteEntranceExit/${get(props, ["section"])}/site/${defaultSite}`
        );
      }
      // Site ID changed
      else if (companiesData) {
        let newSite = get(
          filter(
            flatten(
              map(companiesData, (c) =>
                map(c.sites, (s) => ({ ...s, company_id: c.id }))
              )
            ),
            (s) => s.id === siteId
          ),
          [0]
        );
        if (isEmpty(newSite)) {
          newSite = get(companiesData, [0, "sites", 0]);
        }
        dispatch(selectSite(section, newSite));
        handleSiteSelect(dispatch, section, null, newSite);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, companiesData]);

  // Initialization, called on every section change
  React.useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      dispatch(setBreadcrumb([{ name: I18n.get(`topbar_${section}`) }]));
      dispatch(fetchCompanies(section));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  // Switch from driveInOut / walkInOut
  const urlSection = get(props, ["section"]);
  React.useEffect(() => {
    if (urlSection && urlSection !== section) {
      setInitialized(false);
      setSection(urlSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSection]);

  React.useEffect(() => {
    if (
      eventGroupId &&
      get(props, ["siteEntranceExit", section, "initialized"]) &&
      size(filter(tabs, (t) => t.eventGroupId === eventGroupId)) === 0
    ) {
      createTab();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventGroupId, get(props, ["siteEntranceExit", section, "initialized"])]);

  const createTab = () => {
    dispatch(
      addTab(section, null, {
        type: DYNAMIC_TAB,
        eventGroupId,
      })
    );
  };

  React.useEffect(() => {
    const initializedProps = get(props, ["siteEntranceExit", section, "initialized"]);
    const activeTabIndexProps = get(props, ["siteEntranceExit", section, "activeTab"]);
    if (!initializedProps) return;
    if (!tabs) return;

    // Figure out what the current active tab should be and set it.
    // This should also update things if the content of the currently
    // active tab is updated like when logs are loaded into the tab.
    let defaultActiveTab = get(tabs, [0]);
    let activeTab = defaultActiveTab;
    if (!isNaN(zoneId)) {
      activeTab = get(
        filter(
          tabs,
          (tab) =>
            get(tab, ["type"]) === STATIC_TAB &&
            get(tab, ["zone", "id"]) === zoneId
        ),
        [0]
      );
    }
    if (!isNaN(eventGroupId)) {
      activeTab = get(
        filter(
          tabs,
          (tab) =>
            get(tab, ["type"]) === DYNAMIC_TAB &&
            get(tab, ["eventGroupId"]) === parseInt(eventGroupId)
        ),
        [0]
      );
    }
    setActiveTab(activeTab);

    // is the wrong tab currently selected?
    let activeTabIndex = 0;
    setZone(get(activeTab, ["zone", "id"]));
    if (get(activeTab, ["id"])) {
      forEach(tabs, (t, i) => {
        if (get(t, ["id"]) === get(activeTab, ["id"])) {
          activeTabIndex = i;
        }
      });
    }
    if (activeTabIndex !== activeTabIndexProps) {
      // if so, dispatch the switching of the tab.
      dispatch(selectTab(section, activeTabIndex));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventGroupId, zoneId, tabs, get(props, ["siteEntranceExit", section, "initialized"])]);

  const changeTab = (id, pageParam) => {
    let tab = get(
      filter(tabs, (t) => t.id === id),
      [0]
    );

    if (get(tab, ["type"]) === STATIC_TAB) {
      props.history.push(
        `/siteEntranceExit/${section}/site/${siteId}/zone/${get(tab, [
          "zone",
          "id",
        ])}`
      );
    } else if (get(tab, ["type"]) === DYNAMIC_TAB) {
      props.history.push(
        `/siteEntranceExit/${section}/site/${siteId}/eventGroup/${get(tab, [
          "eventGroupId",
        ])}`
      );
    } else if (get(tab, ["type"]) === LOGS_TAB) {
      let url = `/siteEntranceExit/${section}/site/${siteId}`;
      if (pageParam && !isNaN(parseInt(pageParam))) {
        url += `?page=${pageParam}`;
      }
      props.history.push(url);
    }
  };

  let zoneEvents = {};
  if (parseInt(zone)) {
    if (section === walkInOut) {
      zoneEvents = get(props, [
        "siteEntranceExit",
        section,
        "liveEvents",
        zone,
        "events",
      ]);
    } else if (section === driveInOut) {
      zoneEvents = {
        driver: get(props, [
          "siteEntranceExit",
          section,
          "liveEvents",
          zone,
          "driver",
        ]),
        truck: get(props, [
          "siteEntranceExit",
          section,
          "liveEvents",
          zone,
          "truck",
        ]),
        trailer: get(props, [
          "siteEntranceExit",
          section,
          "liveEvents",
          zone,
          "trailer",
        ]),
        container: get(props, [
          "siteEntranceExit",
          section,
          "liveEvents",
          zone,
          "container",
        ]),
      };
    }
  }

  // Update Events on component update
  React.useEffect(() => {
    if (zoneEvents) {
      updateEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneEvents]);

  let sessionId;
  if (parseInt(zone) && section === driveInOut) {
    sessionId = get(props, [
      "siteEntranceExit",
      section,
      "liveEvents",
      zone,
      "sessionId",
    ]);
  }

  // Refresh log table if session_id changed
  React.useEffect(() => {
    if (sessionId) {
      dispatch(
        getLatestLog(
          section,
          get(props, ["siteEntranceExit", section, "activeTab"])
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const updateEvents = async () => {
    let newState = get(props, [
      "siteEntranceExit",
      section,
      "liveEvents",
      zone,
    ]);
    let shouldUpdateState = false;
    if (section === walkInOut) {
      newState = get(newState, ["events"]);
    }

    let toFetch = [];
    let eventKeys = Object.keys(zoneEvents);
    if (eventKeys && eventKeys.length > 0) {
      for (let i = 0; i < eventKeys.length; i++) {
        let event = zoneEvents[eventKeys[i]];
        let cleaningTimestamp = get(event, ["cleaningTimestamp"]);
        let timer = get(event, ["timer"]);
        let timeout = get(event, ["timeout"]);
        let open = get(event, ["open"]);
        if (section === driveInOut) {
          cleaningTimestamp = get(newState, ["cleaningTimestamp"]);
          timer = get(newState, ["timer"]);
          timeout = get(newState, ["timeout"]);
          open = false;
        }

        if (cleaningTimestamp && cleaningTimestamp <= Date.now()) {
          newState = update(
            newState,
            section === walkInOut
              ? {
                $unset: [eventKeys[i]],
              }
              : {
                $set: {
                  globalState: 0,
                  startTime: 0,
                  driver: { state: absent, id: 0, profile: {} },
                  truck: { state: absent, id: 0, profile: {} },
                  trailer: { state: absent, id: 0, profile: {} },
                  container: { state: absent, id: 0, profile: {} },
                },
              }
          );
          shouldUpdateState = true;
          dispatch(
            getLatestLog(
              section,
              get(props, ["siteEntranceExit", section, "activeTab"])
            )
          );
        } else if (timer && !timeout && !open) {
          let remainingTime = cleaningTimestamp - Date.now();
          let timeout = setTimeout(
            () =>
              liveEventTimeout(
                dispatch,
                section,
                zone,
                event.session_id,
                get(props, ["siteEntranceExit", section, "activeTab"])
              ),
            remainingTime || timer
          );
          toFetch.push(event);
          if (section === walkInOut) {
            newState = update(newState, {
              [eventKeys[i]]: {
                timeout: { $set: timeout },
                cleaningTimestamp: {
                  $set: Date.now() + (remainingTime || timer),
                },
                open: { $set: false },
              },
            });
          } else {
            newState = update(newState, {
              timeout: { $set: timeout },
              cleaningTimestamp: {
                $set: Date.now() + (remainingTime || timer),
              },
              open: { $set: false },
            });
          }
          shouldUpdateState = true;
        } else if (
          event &&
          event.state !== absent &&
          event.event_group_id &&
          !event.dacId &&
          !event.apiCallFailed &&
          !event.isFetching
        ) {
          toFetch.push(event);
          shouldUpdateState = true;
        }
      }

      // Fetch event groups
      if (toFetch.length > 0) {
        newState = await getEventDetails(toFetch, newState);
      }
      if (shouldUpdateState) {
        dispatch(updateLiveEvents(section, zone, newState));
      }
    }
  };

  const getEventDetails = async (events, newState) => {
    let detectionIds = [];
    let dacIds = [];

    events.forEach((e) => {
      if (e.event_group_id) detectionIds.push(e.event_group_id);
    });

    detectionIds = map(detectionIds, parseInt);

    let apiCallFailed = false;

    // Fetch detection event groups
    let detectionEventGroups;
    try {
      if (size(detectionIds) > 0) {
        detectionEventGroups = await fetchPost("new/event_groups", {
          filter: {
            ids: detectionIds,
          },
          data: eventGroupsData,
        });
      }
    } catch (err) {
      console.log(err);
      apiCallFailed = true;
    }

    // Fetch DAC event groups
    let dacEventGroups;
    try {
      events.forEach((e) => {
        let detectionEventGroup = filter(
          get(detectionEventGroups, ["items"]),
          (item) => parseInt(item.id) === parseInt(e.event_group_id)
        )[0];
        let dacId = get(detectionEventGroup, ["parent", "id"]);
        if (dacId) dacIds.push(dacId);
      });

      if (size(dacIds) > 0) {
        dacEventGroups = await fetchPost("new/event_groups", {
          filter: {
            ids: map(dacIds, parseInt),
          },
          data: [
            "id",
            {
              name: "events",
              data: [
                "id",
                "active",
                "created_on",
                "created_by",
                "type",
                "profile",
                "zone",
                "event_count",
                "attributes",
                "notes",
                "images",
                "luke_index",
              ],
            },
          ],
        });
      }
    } catch (err) {
      console.log(err);
      apiCallFailed = true;
    }

    // Measured Temperature and Verification event groups
    let childEventGroups;
    try {
      if (size(dacIds) > 0) {
        childEventGroups = await fetchPost("new/event_groups", {
          filter: {
            parent_ids: map(dacIds, parseInt),
            types: [13, 14],
          },
          data: [
            "id",
            "type",
            "parent",
            {
              name: "events",
              data: [
                "id",
                "active",
                "created_on",
                "created_by",
                "type",
                "profile",
                "zone",
                "event_count",
                "attributes",
                "notes",
                "images",
                "luke_index",
                "reference_event_id",
              ],
            },
          ],
        });
      }
    } catch (err) {
      console.log(err);
      apiCallFailed = true;
    }

    for (let i = 0; i < size(events); i++) {
      let e = get(events, [i]);
      let detectionEventGroup = filter(
        get(detectionEventGroups, ["items"]),
        (item) => parseInt(item.id) === parseInt(e.event_group_id)
      )[0];
      let dacId = get(detectionEventGroup, ["parent", "id"]);
      let dac = get(
        filter(get(dacEventGroups, ["items"]), (item) => item.id === dacId),
        [0]
      );

      let verification;
      try {
        verification = await getVerification(dacId, childEventGroups);
      } catch (err) {
        console.log(err);
        apiCallFailed = true;
      }

      if (section === walkInOut) {
        newState = update(newState, {
          [e.session_id]: {
            apiCallFailed: { $set: apiCallFailed || !dacId },
            detectionEventGroup: { $set: detectionEventGroup },
            dacId: { $set: dacId },
            detection: { $set: getDetection(detectionEventGroup) },
            dac: { $set: dac },
            verification: { $set: verification },
            measuredTemperature: {
              $set: get(
                filter(
                  get(childEventGroups, ["items"]),
                  (e) =>
                    get(e, ["parent", "id"]) === dacId &&
                    get(e, ["type", "id"]) === 13
                ),
                [0]
              ),
            },
          },
        });
      } else {
        if (e.type) {
          newState = update(newState, {
            [e.type]: {
              apiCallFailed: { $set: apiCallFailed || !dacId },
              detectionEventGroup: { $set: detectionEventGroup },
              dacId: { $set: dacId },
              detection: { $set: getDetection(detectionEventGroup) },
              dac: { $set: dac },
            },
          });
        }
      }
    }
    return newState;
  };

  const getVerification = async (dacId, childEventGroups) => {
    const verifEventGroup = get(
      filter(
        get(childEventGroups, ["items"]),
        (e) =>
          get(e, ["parent", "id"]) === dacId && get(e, ["type", "id"]) === 14
      ),
      [0]
    );
    let preSurvey = get(
      filter(get(verifEventGroup, ["events"]), (e) =>
        [91].includes(get(e, ["type", "id"]))
      ),
      [0]
    );
    let lastSurvey = get(
      filter(get(verifEventGroup, ["events"]), (e) =>
        [81].includes(get(e, ["type", "id"]))
      ),
      [0]
    );
    let referenceSurvey;
    if (get(preSurvey, ["reference_event_id"])) {
      referenceSurvey = await fetchGet(
        `events/${get(preSurvey, ["reference_event_id"])}`
      );
    }
    return { ...verifEventGroup, preSurvey, lastSurvey, referenceSurvey };
  };

  const closeTab = async (id) => {
    let openFrom =
      get(
        tabs,
        get(
          filter(tabs, (t) => t.id === id),
          [0, "openFromTab"]
        )
      ) || get(tabs, [0]);

    let pageParam;
    if (get(openFrom, ["type"]) === LOGS_TAB) {
      pageParam = get(openFrom, ["logs", "pageNb"]);
    }

    await dispatch(removeTab(section, id));
    changeTab(get(openFrom, ["id"]), pageParam);
  };

  const urlParams = new URLSearchParams(props.location.search);

  if (!activeTab) {
    return null;
  }

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="height-100"
      style={{
        padding: "12px 24px 24px",
        overflow: "auto",
        maxHeight: "calc(100vh - 100px)",
      }}
    >
      <Grid item>
        <Grid container>
          <Grid item>
            {section === walkInOut && (
              <AddWalkInOut language={props.language} />
            )}
            {section === driveInOut && (
              <AddDriveInOut language={props.language} />
            )}
          </Grid>
          <Grid item style={{ width: "200px" }}>
            <SiteEntranceExitForm section={section} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs className={`${classes.mainContainer}`}>
        <Grid container>
          <Grid item xs>
            <Grid container className={classes.tabContainer}>
              {map(tabs, (tab, index) => (
                <Tab
                  key={tab.id}
                  tab={tab}
                  type={tab.type}
                  active={get(activeTab, ["id"]) === tab.id}
                  closeTab={() => closeTab(tab.id)}
                  onClick={() => changeTab(tab.id)}
                />
              ))}
            </Grid>
          </Grid>
          <Grid item>
            <TabHistory section={section} />
          </Grid>
        </Grid>
        {activeTab.type === LOGS_TAB && (
          <SiteLog
            logs={activeTab.logs}
            site={activeSite}
            openedTabsNb={size(tabs)}
            section={section}
            page={parseInt(urlParams.get("page")) || 0}
          />
        )}
        {activeTab.type === STATIC_TAB && (
          <>
            {section === driveInOut ? (
              <LiveEventsDriveInOut
                entries={
                  get(props, [
                    "siteEntranceExit",
                    section,
                    "liveEvents",
                    activeTab.zone.id,
                  ]) || {}
                }
                zone={activeTab.zone}
                tab={activeTab}
                log={get(props, [
                  "siteEntranceExit",
                  section,
                  "liveEvents",
                  zone
                ]) ? get(props, [
                  "siteEntranceExit",
                  section,
                  "liveEvents",
                  zone
                ]) : {}}
                section={section}
                tabIndex={get(props, ["siteEntranceExit", section, "activeTab"])}
              />
            ) : section === walkInOut ? (
              <LiveEventsWalkInOut
                entries={
                  get(props, [
                    "siteEntranceExit",
                    section,
                    "liveEvents",
                    activeTab.zone.id,
                  ]) || {}
                }
                language={props.language}
              />
            ) : section === trainInOut ? (
              <></> // TODO: LiveTrainUI goes here
            ) : (
              <></>
            )}
            <SiteHistory
              logs={activeTab.logs}
              zone={activeTab.zone}
              tabIndex={
                indexOf(flatten(map(tabs, (t) => t.id)), get(activeTab, ["id"]))
              }
              openedTabsNb={size(tabs)}
              powerUserMode={get(props, ["common", "powerUserModeEnabled"])}
              section={section}
            />
          </>
        )}
        {activeTab.type === DYNAMIC_TAB && (
          <>
            {[walkInOut, driveInOut].includes(section) && (
              <LogDetails
                section={section}
                tab={activeTab}
                log={activeTab.data}
                tabIndex={get(props, ["siteEntranceExit", section, "activeTab"])}
                openFromTab={activeTab.openFromTab}
                zoneId={activeTab.zone ? activeTab.zone.id : ""}
                activeLogDetailsTab={get(props, [
                  "siteEntranceExit",
                  section,
                  "activeLogDetailsTab",
                ])}
              />
            )}
            {[trainInOut].includes(section) && (
              <>
                <TrainDashboard
                  section={section}
                  tabIndex={get(props, ["siteEntranceExit", section, "activeTab"])}
                  openFromTab={activeTab.openFromTab}
                  tab={activeTab}
                  log={activeTab.data}
                  headerTable={""}
                  activeLogDetailsTab={get(props, [
                    "siteEntranceExit",
                    section,
                    "activeLogDetailsTab",
                  ])}
                />
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    siteEntranceExit: state.siteEntranceExit,
    language: state.user.language,
    common: state.common,
    userDataStore: state.userDataStore,
  };
};

export default withRouter(
  connect(mapStateToProps)(withStyles(styles)(SiteEntranceExit))
);
